import { Autocomplete, Box, Button, Grid } from '@mui/material';
import { useDialog } from 'app/context';
import { usePatientDetail } from 'app/hooks';
import { DetailWrapperDiv, InputDatePicker, InputSearchField, SearchCompany, TextField } from 'app/shared';
import { t } from 'i18next';
import { PatientConfirmDeleteDialog } from './dialog/PatientConfirmDeleteDialog';
import { getDefaultJobTitles } from 'app/utils';
import { isEmpty } from 'lodash';

interface Props {
  id: string;
}

export const PatientDetailTab = ({ id }: Props) => {
  const { createDialog, closeDialog } = useDialog();

  const { updateForm, loading, deletePatient, detail } = usePatientDetail(id);

  const { submit, bindTextField, form, setValue, bindError } = updateForm;

  const deleteConfirm = () =>
    createDialog({
      open: true,
      maxWidth: 'sm',
      fullWidth: true,
      children: (
        <PatientConfirmDeleteDialog
          onCancelFn={closeDialog}
          onConfirmFn={() => {
            deletePatient();
            closeDialog();
          }}
        />
      ),
    });

  //search company dialog
  const handleCompanyDialog = () =>
    createDialog({
      open: true,
      maxWidth: 'md',
      fullWidth: true,
      children: (
        <SearchCompany
          allowCreate
          onCancel={closeDialog}
          onConfirmCompany={(company) => {
            setValue({ field: 'company', value: { name: company.name } });
            closeDialog();
          }}
        />
      ),
    });

  return (
    <DetailWrapperDiv>
      <Grid container spacing={4}>
        <Grid size={6}>
          <TextField label={t('patient.lastName')} {...bindTextField({ field: 'last_name' })} />
        </Grid>
        <Grid size={6}>
          <TextField label={t('patient.firstName')} {...bindTextField({ field: 'first_name' })} />
        </Grid>
        <Grid size={6}>
          <TextField label={t('patient.taxCode')} {...bindTextField({ field: 'tax_code' })} />
        </Grid>
        <Grid size={6}>
          <InputDatePicker
            label={t('patient.birthDate')}
            defaultValue={detail?.birth_date ?? null}
            onDatePick={(value) => setValue({ field: 'birth_date', value: value })}
            bindError={() => bindError({ field: 'birth_date' })}
          />
        </Grid>
        <Grid size={6}>
          <InputSearchField
            label={t('patient.company')}
            fieldValue={form.company?.name}
            onSearch={handleCompanyDialog}
            onCancel={() => setValue({ field: 'company', value: undefined })}
          />
        </Grid>
        <Grid size={6}>
          <Autocomplete
            freeSolo
            options={getDefaultJobTitles()}
            value={form.job_title ?? ''}
            onInputChange={(_e, newValue) =>
              setValue({ field: 'job_title', value: isEmpty(newValue) ? undefined : newValue })
            }
            renderInput={(params) => <TextField {...params} label={t('patient.job')} />}
            disableClearable
          />
        </Grid>
        <Grid size={6}>
          <TextField label={t('patient.address')} {...bindTextField({ field: 'residence' })} />
        </Grid>
      </Grid>
      <Box padding={4}>
        <Button sx={{ margin: 1 }} variant="contained" loading={loading} onClick={submit}>
          {t('actions.update')}
        </Button>
        <Button sx={{ margin: 1 }} variant="contained" loading={loading} onClick={deleteConfirm} color="error">
          {t('actions.delete')}
        </Button>
      </Box>
    </DetailWrapperDiv>
  );
};
